import Box from "@/components/Box";
import Divider from "@/components/Divider";
import BloodIcon from "@/images/BloodIcon";
import PenIcon from "@/images/PenIcon";
import SimpleDnaIcon from "@/images/SimpleDnaIcon";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";

function Chart({ inRange, outOfRange, untested }) {
  const total = inRange + outOfRange + untested;
  const inRangeWidth = total > 0 ? `${(inRange / total) * 100}%` : "0%";
  const outOfRangeWidth = total > 0 ? `${(outOfRange / total) * 100}%` : "0%";
  const untestedWidth = total > 0 ? `${(untested / total) * 100}%` : "100%";

  return (
    <Group
      width="100%"
      height={4}
      borderRadius="4.5px"
      overflow="hidden"
      gap={0}
      sx={{ minWidth: 180 }}
    >
      <Box width={inRangeWidth} height="100%" bg="#00BF86" />
      <Box width={outOfRangeWidth} height="100%" bg="#FF5D6D" />
      <Box width={untestedWidth} height="100%" bg="#E5E5E5" />
    </Group>
  );
}

Chart.defaultProps = {
  inRange: 0,
  outOfRange: 0,
  untested: 0
};

export function Title({ assessed, total, pb }) {
  return (
    <Group alignItems="flex-end" gap={12} pb={pb}>
      <Box fontFamily="gilroyRegular" fontSize={[18, 18, 24]} lineHeight={["28px", "28px", "36px"]}>
        Markers Assessed:
      </Box>
      <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight={["32px", "32px", "46px"]}>
        {assessed}/{total}
      </Box>
    </Group>
  );
}

export function InterpretationChart({
  label,
  sx,
  total,
  inRange,
  outOfRange,
  untested = 0,
  icon,
  inRangeLabel = "In range:",
  outOfRangeLabel = "Out of range:",
  untestedLabel = "Untested:"
}) {
  return (
    <Stack {...sx}>
      <Group gap={8}>
        {icon}
        <Box fontFamily="gilroyBold" fontSize={[12, 12, 14]} lineHeight="20px">
          {label}
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[12, 12, 14]} lineHeight="20px">
          {total}
        </Box>
      </Group>
      <Chart inRange={inRange} outOfRange={outOfRange} untested={untested} />
      <Group gap={8} flexWrap="wrap">
        <Group gap={8} alignItems="center">
          <Box width={10} height={10} borderRadius="50%" bg="#00BF86" />
          <Box fontFamily="gilroyBold" fontSize={[12]} lineHeight="20px">
            {inRangeLabel}
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[12]} lineHeight="20px">
            {inRange}
          </Box>
        </Group>
        <Group gap={8} alignItems="center">
          <Box width={10} height={10} borderRadius="50%" bg="#FF5D6D" />
          <Box fontFamily="gilroyBold" fontSize={[12]} lineHeight="20px">
            {outOfRangeLabel}
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[12]} lineHeight="20px">
            {outOfRange}
          </Box>
        </Group>
        <Group gap={8} alignItems="center">
          <Box width={10} height={10} borderRadius="50%" bg="#E5E5E5" />
          <Box fontFamily="gilroyBold" fontSize={[12]} lineHeight="20px">
            {untestedLabel}
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[12]} lineHeight="20px">
            {untested}
          </Box>
        </Group>
      </Group>
    </Stack>
  );
}

function MarkerKey({ label, color, snpValue, functionalValue, symptomValue }) {
  return (
    <Group gap={4} alignItems="center">
      <Box width={10} height={10} borderRadius="50%" bg={color} />
      <Box fontFamily="gilroyBold" fontSize={[12]} lineHeight="20px">
        {label}:
      </Box>
      <Group gap={12}>
        <Group gap={4} alignItems="center">
          <SimpleDnaIcon size={16} withCircle={false} color="black" />
          <Box fontFamily="gilroyMedium" fontSize={[12]} lineHeight="20px">
            {snpValue}
          </Box>
        </Group>
        <Group gap={12} alignItems="center">
          <BloodIcon size={16} withCircle={false} color="black" />
          <Box fontFamily="gilroyMedium" fontSize={[12]} lineHeight="20px">
            {functionalValue}
          </Box>
        </Group>
        <Group gap={12} alignItems="center">
          <PenIcon size={16} withCircle={false} color="black" />
          <Box fontFamily="gilroyMedium" fontSize={[12]} lineHeight="20px">
            {symptomValue}
          </Box>
        </Group>
      </Group>
    </Group>
  );
}

export default function MarkersAssessedChart({
  sx,
  assessed,
  total,
  inRange,
  outOfRange,
  untested = 0,
  snpCounts,
  functionalCounts,
  symptomCounts
}) {
  const resolvedSnpCounts = snpCounts || {
    inRange: inRange || 0,
    outOfRange: outOfRange || 0,
    untested
  };
  const resolvedFunctionalCounts = functionalCounts || {
    inRange: 0,
    outOfRange: 0,
    untested: 0
  };
  const resolvedSymptomCounts = symptomCounts || {
    inRange: inRange || 0,
    outOfRange: outOfRange || 0,
    untested: 0
  };

  const chartInRange =
    resolvedSnpCounts.inRange + resolvedFunctionalCounts.inRange + resolvedSymptomCounts.inRange;
  const chartOutOfRange =
    resolvedSnpCounts.outOfRange +
    resolvedFunctionalCounts.outOfRange +
    resolvedSymptomCounts.outOfRange;
  const chartUntested =
    resolvedSnpCounts.untested + resolvedFunctionalCounts.untested + resolvedSymptomCounts.untested;

  const markersAssessedInRange = resolvedSnpCounts.inRange + resolvedFunctionalCounts.inRange;
  const markersAssessedOutOfRange = resolvedSnpCounts.outOfRange + resolvedFunctionalCounts.outOfRange;
  const markersAssessedUntested = resolvedSnpCounts.untested + resolvedFunctionalCounts.untested;

  const chartAssessed = assessed ?? markersAssessedInRange + markersAssessedOutOfRange;
  const chartTotal = total ?? chartAssessed + markersAssessedUntested;

  return (
    <Stack gap={0} {...sx}>
      <Title assessed={chartAssessed} total={chartTotal} pb="16px" />
      <Chart inRange={chartInRange} outOfRange={chartOutOfRange} untested={chartUntested} />
      <Group gap={16} mt="9px" alignItems="center" flexWrap="wrap">
        <MarkerKey
          label="In range"
          color="#00BF86"
          snpValue={resolvedSnpCounts.inRange}
          functionalValue={resolvedFunctionalCounts.inRange}
          symptomValue={resolvedSymptomCounts.inRange}
        />
        <Divider vertical color="dark" height={15} />
        <MarkerKey
          label="Out of range"
          color="#FF5D6D"
          snpValue={resolvedSnpCounts.outOfRange}
          functionalValue={resolvedFunctionalCounts.outOfRange}
          symptomValue={resolvedSymptomCounts.outOfRange}
        />
        <Divider vertical color="dark" height={15} />
        <MarkerKey
          label="Untested"
          color="#E5E5E5"
          snpValue={resolvedSnpCounts.untested}
          functionalValue={resolvedFunctionalCounts.untested}
          symptomValue={resolvedSymptomCounts.untested}
        />
      </Group>
    </Stack>
  );
}
